import {DecimalPipe, NgStyle} from "@angular/common"
import {Component, computed, ChangeDetectionStrategy} from "@angular/core"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {TextureSet} from "@cm/material-nodes"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {TextureType} from "@generated"
import {BaseImageTextureSetWidgetComponent} from "@material-editor/components/widgets/base-image-texture-set-widget/base-image-texture-set-widget.component"
import {z} from "zod"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-image-texture-set-widget",
    templateUrl: "./image-texture-set-widget.component.html",
    styleUrls: ["./image-texture-set-widget.component.scss"],
    imports: [
        MatMenuModule,
        DecimalPipe,
        MatFormFieldModule,
        MatSelectModule,
        DataObjectThumbnailComponent,
        InputContainerComponent,
        NumericInputComponent,
        NgStyle,
    ],
})
export class ImageTextureSetWidgetComponent extends BaseImageTextureSetWidgetComponent {
    get node() {
        return this.$material() as TextureSet
    }

    async changeTextureSetRevision(revisionId: string | undefined) {
        await this.loadTextureSetRevision(revisionId)
        this.setupTextureInfo()
        this.setThumbnail()
        this.setTextureSetRevisionVersion(revisionId)
        this.updateNodeParameters(true)
    }

    async changeTextureSet(textureSetLegacyId: number | undefined) {
        await this.loadTextureSet(textureSetLegacyId)
        await this.changeTextureSetRevision(this.$textureSetRevisionInfos()?.[0].id)
    }

    async fromNodeParameters() {
        const textureSetRevisionId = this.node.parameters.settings.textureSetRevisionId
        if (!textureSetRevisionId) {
            this.resetParameters()
            return
        }

        await this.loadTextureSetRevision(textureSetRevisionId)
        await this.loadTextureSet(this.$textureSetRevision()?.textureSet?.legacyId)
        this.setTextureSetRevisionVersion(textureSetRevisionId)

        this.updateNodeParameters(false)

        this.setThumbnail()
    }

    readonly $hasTransmissionTexture = computed(() =>
        this.$textureSetRevision()?.mapAssignments.some((mapAssignment) => mapAssignment.textureType === TextureType.Transmission),
    )

    updateNodeParameters(applyDefaults: boolean) {
        this.node.updateSetting("textureSetRevisionId", this.$textureSetRevision()?.id)
        this.node.updateSetting("normalStrength", this.node.parameters.settings.normalStrength)

        if (applyDefaults) {
            this.node.applyDefaults()
        }
        this.triggerGraphUpdate()
    }

    resetNodeParameters() {
        this.node.updateSetting("textureSetRevisionId", undefined)
        this.node.updateSetting("normalStrength", undefined)
    }

    resetParameters() {
        this.$textureSetRevision.set(undefined)
        this.$textureSetLegacyId.set(undefined)
        this.$textureSetRevisionInfos.set(undefined)
        this.$textureSetRevisionVersion.set(undefined)
        this.$thumbnailId.set(undefined)
    }

    setThumbnail() {
        const mapToShowForThumbnail =
            this.$textureSetRevision()?.mapAssignments.find((mapAssignment) => mapAssignment.textureType === TextureType.Diffuse) ??
            this.$textureSetRevision()?.mapAssignments[0]
        this.$thumbnailId.set(mapToShowForThumbnail?.dataObject.id)
    }

    protected readonly z = z
}
